import { useEffect } from 'react';
import { useApp } from '../context.js';
import { todayLocal } from '../lib/date.js';
import { partnerOf } from '../lib/visits.js';
import { wholeOf, isHalf, pickWhole, toggleHalf, formatRating } from '../lib/rating.js';

export default function VisitModal() {
    const { addVisit, closeVisitModal, currentProfile, currentVisit, editingVisitId, haptic, restaurants, setCurrentVisit, showVisitModal, t, visitMode, visitRestaurantId } = useApp();

    const isDate = visitMode === 'date';
    const dirty = currentVisit.rating !== null || !!(currentVisit.notes || '').trim();

    // Escape closes (desktop); on the date flow it counts as "Skip rating"
    useEffect(() => {
        if (!showVisitModal) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') closeVisitModal(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [showVisitModal, closeVisitModal]);

    if (!showVisitModal) return null;

    const restaurant = restaurants.find(r => r.id === visitRestaurantId);
    const partner = partnerOf(currentProfile);
    const partnerName = partner === 'les' ? 'Les' : 'Doug';
    const partnerRated = !editingVisitId && restaurant && (restaurant.visits || []).some(v => v.date === currentVisit.date && v.loggedBy === partner);
    const rating = currentVisit.rating;
    const whole = wholeOf(rating);

    // A stray tap outside must not throw away a half-written visit, and never ends the date flow
    const onBackdrop = () => {
        if (isDate || dirty) return;
        closeVisitModal();
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onBackdrop}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="visit-title"
                className="bg-white apple-card rounded-3xl max-w-md w-full shadow-2xl flex flex-col overflow-hidden"
                style={{ maxHeight: 'calc(100dvh - 2rem - env(safe-area-inset-top) - env(safe-area-inset-bottom))' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 pb-3 overflow-y-auto flex-1 min-h-0">
                    <h2 id="visit-title" className="text-2xl font-bold text-gray-800 mb-1">
                        {isDate ? 'How was it? 🥂' : editingVisitId ? '✏️ Edit Visit' : '📝 Log Visit'}
                    </h2>
                    {restaurant && <p className="text-base font-semibold text-gray-700">{restaurant.name}</p>}
                    <p className="text-sm text-gray-500 mb-4">Logging as {currentProfile === 'les' ? '💖 Les' : '💛 Doug'}</p>
                    {partnerRated && (
                        <p className="text-sm text-pink-600 bg-pink-50 rounded-xl px-3 py-2 mb-4">
                            {partnerName} already rated this night. Saving yours reveals both scores 💕
                        </p>
                    )}

                    <div className="space-y-4">
                        {/* Date */}
                        <div>
                            <label htmlFor="visit-date" className="block text-sm font-bold text-gray-700 mb-2">Date:</label>
                            <input
                                id="visit-date"
                                type="date"
                                value={currentVisit.date}
                                onChange={(e) => setCurrentVisit({...currentVisit, date: e.target.value})}
                                max={todayLocal()}
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none transition-colors text-base"
                            />
                        </div>

                        {/* Rating: 1–10 plus an optional half point */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="block text-sm font-bold text-gray-700">
                                    Rating:{typeof rating === 'number' && <span className={`ml-2 ${t.accentText}`}>{formatRating(rating)}/10</span>}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => { setCurrentVisit({ ...currentVisit, rating: toggleHalf(rating) }); haptic('light'); }}
                                    disabled={typeof rating !== 'number' || rating >= 10}
                                    aria-pressed={isHalf(rating)}
                                    aria-label="Add a half point"
                                    className={`px-4 py-1.5 rounded-full text-sm font-bold apple-button transition-colors ${
                                        isHalf(rating)
                                            ? `bg-gradient-to-r ${t.accent} text-white shadow`
                                            : typeof rating === 'number' && rating < 10
                                                ? 'bg-gray-100 text-gray-700'
                                                : 'bg-gray-100 text-gray-300'
                                    }`}
                                >
                                    +½
                                </button>
                            </div>
                            <div className="grid grid-cols-5 gap-2">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        aria-label={`Rate ${n} out of 10`}
                                        aria-pressed={whole === n}
                                        onClick={() => {
                                            setCurrentVisit({...currentVisit, rating: pickWhole(rating, n)});
                                            haptic('light');
                                        }}
                                        className={`h-12 rounded-xl font-bold text-lg apple-button ${
                                            whole === n
                                                ? `bg-gradient-to-br ${t.accent} text-white shadow-lg`
                                                : 'bg-gray-100 text-gray-600'
                                        }`}
                                    >
                                        {whole === n && isHalf(rating) ? `${n}½` : n}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Notes */}
                        <div>
                            <label htmlFor="visit-notes" className="block text-sm font-bold text-gray-700 mb-2">Notes (optional):</label>
                            <textarea
                                id="visit-notes"
                                value={currentVisit.notes}
                                onChange={(e) => setCurrentVisit({...currentVisit, notes: e.target.value})}
                                placeholder="How was it? What did you order?"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none transition-colors text-base resize-none"
                                rows="3"
                            />
                        </div>
                    </div>
                </div>

                {/* Buttons stay pinned at the bottom, even on small phones or with the keyboard up */}
                <div className="flex gap-2 p-4 pt-3 border-t border-gray-100 bg-white flex-shrink-0" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
                    <button
                        type="button"
                        onClick={closeVisitModal}
                        className="flex-1 bg-gray-200 text-gray-700 px-4 py-3 rounded-xl font-semibold apple-button"
                    >
                        {isDate ? 'Skip rating' : 'Cancel'}
                    </button>
                    <button
                        type="button"
                        onClick={addVisit}
                        disabled={typeof rating !== 'number'}
                        className={`flex-1 px-4 py-3 rounded-xl font-semibold apple-button ${
                            typeof rating === 'number'
                                ? `bg-gradient-to-r ${t.accent} text-white`
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        {editingVisitId ? 'Update Visit' : 'Save Visit'}
                    </button>
                </div>
            </div>
        </div>
    );
}
